import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { api_url } from '../config/config';

const WishlistContext = createContext();

export const useWishlist = () => useContext(WishlistContext);

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchWishlistCount = useCallback(async () => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) { setWishlistCount(0); return 0; }
    try {
      const response = await axios.get(`${api_url}/wishlist-count`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('authToken'),
        },
      });
      const count = response.data?.count || 0;
      setWishlistCount(count);
      return count;
    } catch (err) {
      console.error('Error fetching wishlist count:', err);
      setWishlistCount(0);
      return 0;
    }
  }, []);

  const fetchWishlist = useCallback(async () => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) { setWishlist([]); setWishlistCount(0); return; }
    setLoading(true);
    try {
      const [listResponse] = await Promise.all([
        axios.get(`${api_url}/wishlist`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('authToken'),
          },
        }),
        fetchWishlistCount(),
      ]);

      // API returns: { success: true, data: { wishlistproduct: [...] } }
      const items =
        listResponse.data?.data?.wishlistproduct ||   // ← correct path
        listResponse.data?.list?.wishlistproduct  ||  // ← fallback (old path)
        [];

      setWishlist(items);
    } catch (err) {
      console.error('Error fetching wishlist:', err);
      setWishlist([]);
      setWishlistCount(0);
    } finally {
      setLoading(false);
    }
  }, [fetchWishlistCount]);

  useEffect(() => {
    fetchWishlist();
    const handleAuthChanged = () => fetchWishlist();
    window.addEventListener('authChanged', handleAuthChanged);
    return () => window.removeEventListener('authChanged', handleAuthChanged);
  }, [fetchWishlist]);

  const addToWishlist = async (product, variant) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) { toast.error('Please login first to add item in wishlist'); return; }
    try {
      const response = await axios.post(
        `${api_url}/wishlist`,
        { product_id: product.product_id, variant_id: variant.variant_id },
        { headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('authToken') } }
      );
      if (response.data && !response.data.error) {
        toast.success('Added to wishlist successfully');
        await fetchWishlist();
        await fetchWishlistCount();
      } else {
        toast.error(response.data?.message || 'Failed to add to wishlist');
      }
    } catch (err) {
      toast.error('Error adding to wishlist');
    }
  };

  const removeFromWishlist = async (productId, variantId) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) { toast.error('Please login first'); return; }
    try {
      const response = await axios.delete(
        `${api_url}/wishlist?product_id=${productId}&variant_id=${variantId}`,
        { headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('authToken') } }
      );
      if (response.data && !response.data.error) {
        toast.success('Removed from wishlist');
        await fetchWishlist();
        await fetchWishlistCount();
      } else {
        toast.error(response.data?.message || 'Failed to remove from wishlist');
      }
    } catch (err) {
      toast.error('Error removing from wishlist');
    }
  };

  const isInWishlist = (productId) => wishlist.some((item) => item.product_id === productId);

  return (
    <WishlistContext.Provider value={{
      wishlist,
      wishlistCount,
      loading,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      fetchWishlist,
      fetchWishlistCount,
    }}>
      {children}
    </WishlistContext.Provider>
  );
};